import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

import AutoComplete from "@/components/hookForms/autoComplete";
import messages from "@/utils/messages";
import { Title } from "@/components/title/style";
import {
    classeOptions,
    estadosOptions,
    statusOptions,
    tipoCalculoDialogOptions,
    tipoRebateDialogOptions,
} from "@/utils/constants";
import InputNumber from "@/components/hookForms/inputNumber";
import AutoCompleteCheckbox from "@/components/hookForms/autoCompleteCheckbox";
import serviceRebalanceUsina from "@/services/serviceRebalanceUsina";
import IGenericResult from "@/interfaces/IGenericResult";
import TextInput from "@/components/hookForms/textField";
import serviceParametrizacaoRebate from "@/services/serviceParametrizacaoRebate";
import { LoadingButton } from "@mui/lab";

export interface IDialogProps {
    open: boolean;
    onSuccess: () => void;
    onClose?: any;
    concessionariaDataOptions: IGenericResult[];
}
const Modal: React.FC<IDialogProps> = ({ open, concessionariaDataOptions, onClose, onSuccess }) => {
    const [isDisable, setDisable] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [percentualDisable, setPercentualDisable] = useState(false);
    const [usinaDataOptions, setUsinaDataOptions] = useState<any[]>([]);
    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const requiredFields = [
        { name: "tipoRebate", label: "Tipo Rebate" },
        { name: "tipoCalculo", label: "Tipo de Cálculo" },
        { name: "ativo", label: "Status" },
        { name: "valorDescontoInicial", label: "Desconto - De" },
        { name: "valorDescontoFinal", label: "Desconto - Até" },
        { name: "concessionariaId", label: "Concessionária" },
        { name: "referenciaRebalance", label: "Tipo Pesquisa Cliente" },
    ];

    const {
        control,
        handleSubmit,
        reset,
        resetField,
        formState: { errors },
    } = useForm({
        defaultValues: {
            tipoRebate: 0,
            tipoCalculo: 0,
            concessionariaId: 0,
            ativo: "Ativo",
            classe: "",
            estados: [],
            observacao: "",
            valorDescontoInicial: "",
            valorDescontoFinal: "",
            percentualDesconto: "",
            usinasIds: [],
            referenciaRebalance: "",
        },
    });

    useEffect(() => {
        if (open) {
            reset({
                tipoRebate: 0,
                tipoCalculo: 0,
                concessionariaId: 0,
                ativo: "Ativo",
                classe: "",
                estados: [],
                observacao: "",
                valorDescontoInicial: "",
                valorDescontoFinal: "",
                percentualDesconto: "",
                usinasIds: [],
                referenciaRebalance: "",
            });
        }
    }, [open, reset]);

    const getUsinaData = async (values: any) => {
        if (values.concessionariaID) {
            serviceRebalanceUsina
                .getUsinasByConcessionaria(values)
                .then((result) => {
                    if (result) {
                        setUsinaDataOptions(result?.data);
                    }
                })
                .catch(() => {
                    setUsinaDataOptions([]);
                });
        }
    };

    const onSubmit = (values: any) => {
        setDisable(true);
        setLoading(true);

        const requiredFieldsToCheck = percentualDisable ? requiredFields.slice(-2) : requiredFields;

        const missingFields = requiredFieldsToCheck
            .filter((field) => {
                if (Array.isArray(values[field.name])) {
                    return values[field.name].length === 0;
                }
                return !values[field.name];
            })
            .map((field) => field.label);

        if (missingFields.length > 0) {
            toast.error(`Por favor, preencha os seguintes campos: ${missingFields.join(", ")}`);
            setDisable(false);
            setLoading(false);
            return;
        }

        const newValues = {
            ...values,
            ativo: values.ativo === "Ativo" ? true : false,
            classe: values.classe ?? null,
            tipoRebate: values.tipoRebate ? values.tipoRebate - 1 : null,
            tipoCalculo: values.tipoCalculo ? values.tipoCalculo - 1 : null,
            valorDescontoInicial: values.valorDescontoInicial
                ? Number(values.valorDescontoInicial.replaceAll(".", "").replaceAll(",", "."))
                : null,
            valorDescontoFinal: values.valorDescontoFinal
                ? Number(values.valorDescontoFinal.replaceAll(".", "").replaceAll(",", "."))
                : null,
            percentualDesconto: values.percentualDesconto
                ? Number(values.percentualDesconto.replaceAll(".", "").replaceAll(",", "."))
                : null,
        };

        serviceParametrizacaoRebate
            .create(newValues)
            .then((result) => {
                if (result) {
                    toast.success(messages.createSucess);
                    onClose();
                    onSuccess();
                    reset();
                }
            })
            .catch((err) => {
                toast.error(messages.createError, err);
            })
            .finally(() => {
                setLoading(false);
                setDisable(false);
            });
    };

    return (
        <Box>
            <Dialog
                maxWidth="md"
                fullWidth
                onClose={(_, reason) => {
                    if (reason !== "backdropClick") {
                        onClose();
                    }
                }}
                open={open}
            >
                <DialogTitle>
                    <Title>Novo Parâmetro de Rebate</Title>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            reset();
                            onClose();
                        }}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="tipoRebate"
                                    label="Tipo Rebate"
                                    options={tipoRebateDialogOptions}
                                    control={control}
                                    error={errors}
                                    onInputChange={(_, value, __) => {
                                        value === "Sem Rebate"
                                            ? setPercentualDisable(true)
                                            : setPercentualDisable(false);
                                    }}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="tipoCalculo"
                                    label="Tipo de Cálculo"
                                    options={tipoCalculoDialogOptions}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="concessionariaId"
                                    label="Concessionária"
                                    options={concessionariaDataOptions}
                                    control={control}
                                    error={errors}
                                    onInputChange={(_, __, reason) => {
                                        if (reason === "clear") {
                                            setUsinaDataOptions([]);
                                            resetField("usinasIds");
                                        }
                                    }}
                                    onChange={(idConcessionaria) => {
                                        setUsinaDataOptions([]);
                                        resetField("usinasIds");
                                        if (idConcessionaria) {
                                            getUsinaData({ concessionariaID: idConcessionaria });
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <AutoCompleteCheckbox
                                    name="usinasIds"
                                    label="Usinas"
                                    options={usinaDataOptions ?? []}
                                    control={control}
                                    error={errors}
                                    limitTags={1}
                                    disableCloseOnSelect={true}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                            {option.label}
                                        </li>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputNumber
                                    name="referenciaRebalance"
                                    label="Tipo Pesquisa Cliente"
                                    control={control}
                                    error={errors}
                                    decimalScale={0}
                                    maxLength={3}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="classe"
                                    label="Classe"
                                    options={classeOptions}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <AutoCompleteCheckbox
                                    name="estados"
                                    label="Estados"
                                    options={estadosOptions}
                                    control={control}
                                    error={errors}
                                    limitTags={1}
                                    disableCloseOnSelect={true}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                            {option.label}
                                        </li>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputNumber
                                    name="valorDescontoInicial"
                                    label="Desconto - De"
                                    control={control}
                                    error={errors}
                                    decimalScale={0}
                                    maxLength={9}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputNumber
                                    name="valorDescontoFinal"
                                    label="Desconto - Até"
                                    control={control}
                                    error={errors}
                                    decimalScale={2}
                                    maxLength={9}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputNumber
                                    name="percentualDesconto"
                                    label="Rebate %"
                                    control={control}
                                    error={errors}
                                    decimalScale={2}
                                    maxLength={9}
                                    disabled={percentualDisable}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="ativo"
                                    label="Status"
                                    options={statusOptions.slice(1)}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <TextInput name="observacao" label="Observação" control={control} error={errors} />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                        <Button
                            onClick={() => {
                                reset();
                                onClose();
                            }}
                            color="warning"
                            variant="contained"
                            style={{ width: "159px", height: "44px" }}
                        >
                            Cancelar
                        </Button>
                        <LoadingButton
                            type="submit"
                            color="primary"
                            variant="contained"
                            loading={isLoading}
                            disabled={isDisable}
                            style={{ width: "159px", height: "44px" }}
                        >
                            Salvar
                        </LoadingButton>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default Modal;
